import {announce} from '@github-ui/aria-live'
import {
  CONNECTION_MUTATION_KEY_ROOT,
  type ConnectionMutationVariables,
} from '@github-ui/optimistic-connection/pendingOperations'
import {registerSettlementHandler} from '@github-ui/optimistic-connection/settlementRegistry'
import {type Query, type QueryClient, queryOptions, skipToken, useQuery, useQueryClient} from '@github-ui/react-query'
import {useCallback, useEffect, useMemo, useRef, useSyncExternalStore} from 'react'

import type {FieldDefinition} from '../../saved-view-options/fields'
import {isBoardItemEdit} from '../board-document-adapter'
import type {SavedViewsBoardDeferredFieldsSource} from '../types'
import type {BoardPayloadItem} from './board-payload'
import type {BoardDeferredFields} from './deferred-payload'

export const DEFERRED_CARD_FIELDS_QUERY_PREFIX = 'saved-views-board-deferred'
const DEFERRED_CARD_FIELDS_STALE_TIME_MS = 60_000
/**
 * The shared client turns retries off. Items that fail together retry together, on the same
 * backoff, allowing the batch loader to collect their retries into new batches.
 */
const DEFERRED_CARD_FIELDS_RETRIES = 2
const NO_FIELD_DEFINITIONS: readonly FieldDefinition[] = []

/**
 * Loads fields omitted from the initial board payload. Values are cached by view and item ID.
 */
export type DeferredCardFieldsSource = SavedViewsBoardDeferredFieldsSource<BoardDeferredFields>

/**
 * Reads the same item cache filled when items enter the board. A card can also load a missing
 * entry itself, for standalone rendering or when an offscreen entry has been garbage-collected.
 *
 * Data is `undefined` until it arrives, and always without a source.
 */
export function useDeferredCardFields(
  item: BoardPayloadItem,
  visibleFieldIdentifiers: ReadonlySet<string>,
  fieldDefinitions: readonly FieldDefinition[] = [],
  source?: DeferredCardFieldsSource,
) {
  const shown = showsDeferredField(item, visibleFieldIdentifiers, fieldDefinitions)

  const query = useQuery({
    ...deferredCardFieldsQueryOptions(item.globalId, source),
    ...(shown ? {} : {queryFn: skipToken}),
  })

  return {
    data: source && shown ? query.data : undefined,
    hasFailed: Boolean(source && shown && query.isError && !query.isFetching && query.data === undefined),
  }
}

// Checks whether a deferred field is toggled on, to determine if deferred fetching should occur at all.
function showsDeferredField(
  item: BoardPayloadItem,
  visibleFieldIdentifiers: ReadonlySet<string>,
  fieldDefinitions: readonly FieldDefinition[],
): boolean {
  return fieldDefinitions.some(
    ({identifier, deferred, capabilities}) =>
      deferred === true &&
      visibleFieldIdentifiers.has(identifier) &&
      (capabilities.attributable ?? []).includes(item.type),
  )
}

function deferredCardFieldsQueryOptions(globalId: string, source?: DeferredCardFieldsSource) {
  return queryOptions({
    queryKey: [DEFERRED_CARD_FIELDS_QUERY_PREFIX, ...(source?.scope ?? []), globalId],
    queryFn: source ? async (): Promise<BoardDeferredFields> => (await source.load(globalId)) ?? {} : skipToken,
    staleTime: DEFERRED_CARD_FIELDS_STALE_TIME_MS,
    retry: DEFERRED_CARD_FIELDS_RETRIES,
  })
}

function hasMissingDeferredFields(query: Query): boolean {
  return query.state.status === 'error' && query.state.fetchStatus === 'idle' && query.state.data === undefined
}

/**
 * Marks an item's deferred fields stale once the server confirms an edit to the item.
 *
 * Deferred values stay unchanged while saving. Refetching after any confirmed item edit avoids
 * mapping mutation fields to deferred field identifiers.
 */
export function useInvalidateEditedDeferredCardFields(
  source: DeferredCardFieldsSource | undefined,
  fieldDefinitions: readonly FieldDefinition[] = NO_FIELD_DEFINITIONS,
): void {
  const queryClient = useQueryClient()
  const hasDeferredFields = fieldDefinitions.some(({deferred}) => deferred === true)

  useEffect(() => {
    if (!source || !hasDeferredFields) return

    const unregister = registerSettlementHandler(async operation => {
      if (isBoardItemEdit(operation)) await markDeferredCardFieldsStale(queryClient, source, operation.id)
    })

    return () => {
      unregister()
      markStaleAfterPendingEdits(queryClient, source)
    }
  }, [hasDeferredFields, queryClient, source])
}

async function markDeferredCardFieldsStale(
  queryClient: QueryClient,
  source: DeferredCardFieldsSource,
  globalId: string,
): Promise<void> {
  const query = {queryKey: [DEFERRED_CARD_FIELDS_QUERY_PREFIX, ...source.scope, globalId], exact: true}
  // Invalidation alone reuses an initial request that has no cached data yet.
  await queryClient.cancelQueries(query)
  // Not awaited, so the edit's mutation doesn't wait for the card's refetch. Invalidation marks
  // it stale synchronously.
  void queryClient.invalidateQueries(query)
}

export function useInvalidateRefreshedDeferredCardFields(
  source: DeferredCardFieldsSource | undefined,
): (ids: readonly string[]) => void {
  const queryClient = useQueryClient()
  return useCallback(
    ids => {
      if (!source) return
      for (const id of ids) void markDeferredCardFieldsStale(queryClient, source, id)
    },
    [queryClient, source],
  )
}

/**
 * Ensures that the item's deferred fields are marked stale and refetched, once the edit
 * completes even if board unmounts.
 */
function markStaleAfterPendingEdits(queryClient: QueryClient, source: DeferredCardFieldsSource): void {
  const mutationCache = queryClient.getMutationCache()
  const pending = new Map<number, string>()
  for (const mutation of mutationCache.findAll({status: 'pending', mutationKey: [CONNECTION_MUTATION_KEY_ROOT]})) {
    const operation = (mutation.state.variables as ConnectionMutationVariables | undefined)?.operation
    if (operation && isBoardItemEdit(operation)) pending.set(mutation.mutationId, operation.id)
  }
  if (pending.size === 0) return

  const unsubscribe = mutationCache.subscribe(event => {
    const {mutation} = event
    const globalId = mutation && pending.get(mutation.mutationId)
    if (!mutation || globalId === undefined) return
    if (event.type !== 'removed' && mutation.state.status === 'pending') return

    pending.delete(mutation.mutationId)
    if (mutation.state.status === 'success') void markDeferredCardFieldsStale(queryClient, source, globalId)
    if (pending.size === 0) unsubscribe()
  })
}

/** Announce once per board failure episode, not once per card in a failed batch. */
export function useAnnounceDeferredCardFieldFailures(source: DeferredCardFieldsSource | undefined): void {
  const queryCache = useQueryClient().getQueryCache()
  const queryKey = useMemo(() => [DEFERRED_CARD_FIELDS_QUERY_PREFIX, ...(source?.scope ?? [])], [source])
  const hasFailures = useSyncExternalStore(
    queryCache.subscribe,
    () =>
      source !== undefined &&
      queryCache.findAll({queryKey, type: 'active', predicate: hasMissingDeferredFields}).length > 0,
    () => false,
  )

  useEffect(() => {
    if (hasFailures) announce("Some cards couldn't load all details.")
  }, [hasFailures])
}

/** Requests newly eligible loaded items without waiting for their cards to enter the viewport. */
export function useLoadedDeferredCardFields(
  source: DeferredCardFieldsSource | undefined,
  visibleFieldIdentifiers: ReadonlySet<string>,
  fieldDefinitions: readonly FieldDefinition[] = NO_FIELD_DEFINITIONS,
): (items: readonly BoardPayloadItem[]) => void {
  const queryClient = useQueryClient()
  const requestedRef = useRef({source, ids: new Set<string>()})

  return useCallback(
    (items: readonly BoardPayloadItem[]) => {
      if (!source) return
      // A request returns every deferred field for an item, so a field change only adds newly eligible items.
      if (requestedRef.current.source !== source) requestedRef.current = {source, ids: new Set<string>()}
      for (const item of items) {
        if (
          requestedRef.current.ids.has(item.globalId) ||
          !showsDeferredField(item, visibleFieldIdentifiers, fieldDefinitions)
        ) {
          continue
        }
        requestedRef.current.ids.add(item.globalId)
        // Prefetch preserves failures in the item query, independently of the successful cell page.
        void prefetchDeferredCardFields(queryClient, deferredCardFieldsQueryOptions(item.globalId, source))
      }
    },
    [fieldDefinitions, queryClient, source, visibleFieldIdentifiers],
  )
}

async function prefetchDeferredCardFields(
  queryClient: QueryClient,
  options: ReturnType<typeof deferredCardFieldsQueryOptions>,
): Promise<void> {
  try {
    await queryClient.query(options)
  } catch {
    // The failure stays in the item's query, where its card reads it.
  }
}
